// ─── ValidateTab ──────────────────────────────────────────────────────────────
// Data Analytics → Validate sub-tab (XD layout + workflow).
//   Toolbar: zoom, status pills (show/hide colouring), and 5 right-side tools:
//     🔍 Find and Mark · 📋 Mark Rules · ➕ Add Comment · 💬 Comments · 🕘 History
//   Chart (real traces) above a full spreadsheet of real data with cells coloured
//   by QA/QC status (from manual Find-and-Mark + saved Mark Rules).
//
// Persistence: marks / rules / comments / history are saved to localStorage so
// they survive reload (per browser). Server-side/shared persistence would need
// backend tables + endpoints (not present yet).
// ──────────────────────────────────────────────────────────────────────────────

import { useState, useMemo, useRef, useEffect } from 'react'
import Spreadsheet from 'react-spreadsheet'
import { ChevronDown, Search, ClipboardList, MessageSquarePlus, MessageSquare, History, X } from 'lucide-react'
import { DynamicGraph } from '../../DynamicGraph'
import { useVisualizeData } from './useVisualizeData'
import { loadValidate, saveValidate, markKey, matchCondition, CONDITIONS, uid } from './validatePersistence'

const COL_W = 150, ROW_H = 28, ROWHDR_W = 44, MIN_ROWS = 22, HEADER_ROWS = 3

const STATUS_CONFIG = [
    { id: 'provisional', label: 'Provisional', bg: '#FFFFFF', text: '#374151', border: '#D1D5DB', dropdown: false },
    { id: 'outlier',     label: 'Outlier',     bg: '#FCEFA0', text: '#7C5800', border: '#F1D862' },
    { id: 'suspect',     label: 'Suspect',     bg: '#F5BC83', text: '#7C3A00', border: '#E89A57' },
    { id: 'erroneous',   label: 'Erroneous',   bg: '#F1948A', text: '#7A1F1F', border: '#E3776E' },
    { id: 'reviewed',    label: 'Reviewed',    bg: '#D6B8E0', text: '#523070', border: '#BD93CC' },
    { id: 'verified',    label: 'Verified',    bg: '#A7DCA4', text: '#2A5A28', border: '#7DC57A' },
    { id: 'corrected',   label: 'Corrected',   bg: '#9EC9EE', text: '#1A4480', border: '#7AAFE0' },
    { id: 'backfilled',  label: 'Backfilled',  bg: '#D5D5D5', text: '#3F3F3F', border: '#BDBDBD' },
]
// Mark As offers all 8 statuses (spec). Marking as "provisional" clears the mark.
const MARK_AS = STATUS_CONFIG
const now = () => new Date().toLocaleString('en-US', { hour12: true })
const colLetter = (i) => { let s = ''; i += 1; while (i > 0) { const m = (i - 1) % 26; s = String.fromCharCode(65 + m) + s; i = Math.floor((i - 1) / 26) } return s }

export default function ValidateTab({ selectedParameters = [], dateRangeGraph, activePeriod }) {
    const [zoom, setZoom] = useState('100%')
    const [enabledStatuses, setEnabledStatuses] = useState(Object.fromEntries(STATUS_CONFIG.map((s) => [s.id, true])))
    const [panel, setPanel] = useState('comments')   // right dock: 'comments' | 'history' | null
    const [modal, setModal] = useState(null)          // 'find' | 'rules' | 'comment' | null
    const [vstate, setVstate] = useState(loadValidate)
    const [active, setActive] = useState(null) // active grid cell {row, column}

    const { data, series, loading } = useVisualizeData(selectedParameters, dateRangeGraph, activePeriod)
    const zoomFactor = parseInt(zoom, 10) / 100

    // Each history entry snapshots the state so it can be rolled back to.
    const commit = (next, historyText) => {
        const merged = historyText
            ? { ...next, history: [{ id: uid('h'), time: now(), text: historyText, snapshot: { marks: next.marks, rules: next.rules, comments: next.comments } }, ...(next.history || [])] }
            : next
        setVstate(merged); saveValidate(merged)
    }
    const rollback = (entry) => {
        if (!entry.snapshot) return
        commit({ marks: entry.snapshot.marks || {}, rules: entry.snapshot.rules || [], comments: entry.snapshot.comments || [], history: vstate.history }, `Rolled back to: ${entry.text}`)
    }

    const sheetRef = useRef(null)
    const [box, setBox] = useState({ w: 0, h: 0 })
    useEffect(() => {
        const el = sheetRef.current
        if (!el || typeof ResizeObserver === 'undefined') return
        const ro = new ResizeObserver((e) => { const cr = e[0].contentRect; setBox({ w: cr.width, h: cr.height }) })
        ro.observe(el); return () => ro.disconnect()
    }, [])

    // Effective status for a value cell: manual mark wins, else first matching rule.
    const statusFor = (paramId, label, value) => {
        const m = vstate.marks[markKey(paramId, label)]
        if (m) return m
        for (const r of vstate.rules) {
            if (r.paramId === String(paramId) && matchCondition(value, r.condition, r.v1, r.v2)) return r.status
        }
        return null
    }

    const gridData = useMemo(() => {
        if (!data.length) return []
        const dataCols = data.reduce((m, r) => Math.max(m, r.length), 0)
        const blank = { value: '', readOnly: true }
        const fitCols = Math.max(0, Math.floor((box.w / zoomFactor - ROWHDR_W) / COL_W))
        const fitRows = Math.max(0, Math.floor((box.h / zoomFactor - ROW_H) / ROW_H))
        const totalCols = Math.max(dataCols, fitCols, 1)
        const totalRows = Math.max(data.length, fitRows, MIN_ROWS)
        const commentedKeys = new Set(vstate.comments.map((c) => markKey(c.paramId, c.label)))
        const out = []
        for (let r = 0; r < totalRows; r++) {
            const src = data[r]
            const row = new Array(totalCols)
            for (let c = 0; c < totalCols; c++) {
                const cell = src && src[c]
                const base = { value: cell?.value ?? '', readOnly: true }
                if (cell && r >= HEADER_ROWS && c >= 1 && cell.value !== '' && !isNaN(Number(cell.value))) {
                    const param = series[c - 1]
                    const label = src[0]?.value
                    const st = param ? statusFor(param.id, label, Number(cell.value)) : null
                    const cls = [
                        st && enabledStatuses[st] ? `da-${st}` : '',
                        param && commentedKeys.has(markKey(param.id, label)) ? 'da-comment' : '',
                    ].filter(Boolean).join(' ')
                    row[c] = cls ? { ...base, className: cls } : base
                } else {
                    row[c] = cell ? base : blank
                }
            }
            out.push(row)
        }
        return out
    }, [data, series, vstate, enabledStatuses, box.w, box.h, zoomFactor])

    // Apply a (paramId, condition, values, status) over the loaded data → marks.
    const applyMark = (paramId, condition, v1, v2, status, ruleName) => {
        const col = series.findIndex((s) => String(s.id) === String(paramId)) + 1
        if (col <= 0) return 0
        const marks = { ...vstate.marks }
        let count = 0
        for (let r = HEADER_ROWS; r < data.length; r++) {
            const v = data[r]?.[col]?.value
            const label = data[r]?.[0]?.value
            if (v !== '' && !isNaN(Number(v)) && matchCondition(Number(v), condition, v1, v2)) {
                if (status === 'provisional') delete marks[markKey(paramId, label)] // clear → raw
                else marks[markKey(paramId, label)] = status
                count++
            }
        }
        const pname = series[col - 1]?.name || paramId
        commit({ ...vstate, marks }, `${ruleName || 'Find & Mark'}: marked ${count} point(s) of "${pname}" as ${status}`)
        return count
    }

    const togglePill = (id) => setEnabledStatuses((p) => ({ ...p, [id]: !p[id] }))
    const hasParams = selectedParameters.length > 0
    const iconBtn = (active) => `p-1.5 rounded ${active ? 'text-[#358ECD] bg-[#E8F1F8]' : 'text-gray-600 hover:bg-gray-100'}`

    return (
        <div className="flex flex-col gap-3 flex-1 px-4 pb-4 min-h-0" style={{ fontFamily: "'Open Sans', sans-serif" }}>
            {/* Toolbar — no undo/redo (matches XD) */}
            <div className="flex items-center gap-3 bg-[#F1F2F4] rounded-lg px-3 py-2 flex-wrap">
                <select value={zoom} onChange={(e) => setZoom(e.target.value)} className="bg-gray-50 border border-gray-200 px-2 py-1 rounded text-xs text-gray-700 hover:bg-gray-100 focus:outline-none flex-shrink-0">
                    {['50%', '75%', '100%', '125%', '150%'].map((z) => <option key={z} value={z}>{z}</option>)}
                </select>

                <div className="flex items-center gap-1.5 flex-wrap">
                    {STATUS_CONFIG.map((s) => {
                        const enabled = enabledStatuses[s.id]
                        return (
                            <button key={s.id} type="button" onClick={() => togglePill(s.id)} title={enabled ? 'Hide' : 'Show'}
                                className="flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium transition border"
                                style={{ backgroundColor: enabled ? s.bg : '#FFFFFF', color: s.text, borderColor: s.border, opacity: enabled ? 1 : 0.5 }}>
                                <span>{s.label}</span>
                                {s.dropdown !== false && <ChevronDown className="w-3 h-3" />}
                            </button>
                        )
                    })}
                </div>

                <div className="flex items-center gap-1 ml-auto flex-shrink-0">
                    <button type="button" title="Find and Mark" onClick={() => setModal('find')} className={iconBtn(modal === 'find')}><Search className="w-4 h-4" /></button>
                    <button type="button" title="Mark Rules" onClick={() => setModal('rules')} className={iconBtn(modal === 'rules')}><ClipboardList className="w-4 h-4" /></button>
                    <button type="button" title="Add Comment" onClick={() => setModal('comment')} className={iconBtn(modal === 'comment')}><MessageSquarePlus className="w-4 h-4" /></button>
                    <button type="button" title="Comments" onClick={() => setPanel(panel === 'comments' ? null : 'comments')} className={iconBtn(panel === 'comments')}><MessageSquare className="w-4 h-4" /></button>
                    <button type="button" title="Version History" onClick={() => setPanel(panel === 'history' ? null : 'history')} className={iconBtn(panel === 'history')}><History className="w-4 h-4" /></button>
                </div>
            </div>

            <div className="flex gap-3 flex-1 min-h-0">
                <div className="flex flex-col gap-3 flex-1 min-w-0">
                    <div className="bg-white border border-gray-200 rounded p-3" style={{ minHeight: 240 }}>
                        {hasParams ? (
                            <DynamicGraph selectedParameters={selectedParameters} dateRange={dateRangeGraph} timezone="UTC" timePeriod={activePeriod} />
                        ) : (
                            <div className="h-48 flex items-center justify-center text-xs text-gray-500">Select parameters in the sidebar to plot a trace.</div>
                        )}
                    </div>
                    <div ref={sheetRef} className="flex-1 bg-white border border-gray-200 overflow-auto min-h-0">
                        {!hasParams ? (
                            <div className="h-full flex items-center justify-center text-sm text-gray-400 p-8 text-center">Select one or more parameters to validate.</div>
                        ) : loading ? (
                            <div className="h-full flex items-center justify-center gap-2 text-sm text-gray-500">Loading data…</div>
                        ) : (
                            <div style={{ zoom: zoomFactor }} className="dataanalytics-sheet"><Spreadsheet data={gridData} onChange={() => {}} onActivate={(pt) => setActive(pt)} /></div>
                        )}
                    </div>
                </div>

                {panel === 'comments' && <CommentsDock comments={vstate.comments} onClose={() => setPanel(null)} />}
                {panel === 'history' && <HistoryDock history={vstate.history} onRollback={rollback} onClose={() => setPanel(null)} />}
            </div>

            {modal === 'find' && (
                <FindAndMarkModal series={series} onCancel={() => setModal(null)}
                    onMark={({ paramId, condition, v1, v2, status }) => { const n = applyMark(paramId, condition, v1, v2, status); setModal(null); alert(`Marked ${n} point(s) as ${status}.`) }} />
            )}
            {modal === 'rules' && (
                <MarkRulesModal series={series} rules={vstate.rules} onCancel={() => setModal(null)}
                    onSave={(rules) => { commit({ ...vstate, rules }, `Updated mark rules (${rules.length})`); setModal(null) }} />
            )}
            {modal === 'comment' && (
                <AddCommentModal cellLabel={active && active.row >= HEADER_ROWS && active.column >= 1 ? `${colLetter(active.column)}${active.row + 1}` : null} onCancel={() => setModal(null)}
                    onAdd={(body, attachments) => {
                        let cellRef = '', paramId = '', label = ''
                        if (active && data.length && active.row >= HEADER_ROWS && active.column >= 1) {
                            const param = series[active.column - 1]
                            label = data[active.row]?.[0]?.value
                            if (param && label != null) { paramId = param.id; cellRef = `${colLetter(active.column)}${active.row + 1}` }
                        }
                        const c = { id: uid('c'), author: 'You', cellRef, paramId, label, timestamp: now(), body, attachments: attachments || [] }
                        commit({ ...vstate, comments: [c, ...vstate.comments] }, `Added a comment${cellRef ? ' on ' + cellRef : ''}`)
                        setModal(null); setPanel('comments')
                    }} />
            )}
        </div>
    )
}

// ── Right docks ──
function CommentsDock({ comments, onClose }) {
    const [q, setQ] = useState('')
    const filtered = comments.filter((c) => !q || c.body.toLowerCase().includes(q.toLowerCase()) || c.author.toLowerCase().includes(q.toLowerCase()))
    return (
        <div className="w-72 flex-shrink-0 bg-white border border-gray-200 rounded flex flex-col">
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
                <h3 className="text-sm font-bold text-gray-800">Comments</h3>
                <button onClick={onClose}><X className="w-4 h-4 text-gray-400 hover:text-gray-600" /></button>
            </div>
            <div className="p-3 border-b border-gray-100">
                <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search Comments" className="w-full text-xs border border-gray-300 rounded px-2 py-1.5 focus:outline-none focus:border-[#358ECD]" />
            </div>
            <div className="flex-1 overflow-auto p-3 space-y-4">
                {filtered.length === 0 && <p className="text-xs text-gray-400 text-center mt-6">No comments yet.</p>}
                {filtered.map((c) => (
                    <div key={c.id}>
                        <div className="flex items-baseline gap-2">
                            <span className="text-sm font-semibold text-gray-800">{c.author}</span>
                            {c.cellRef && <span className="text-[10px] font-medium text-[#358ECD] bg-[#E8F1F8] px-1.5 rounded">{c.cellRef}</span>}
                            <span className="text-[10px] text-gray-400">{c.timestamp}</span>
                        </div>
                        {c.body && <p className="text-xs text-gray-600 mt-0.5 whitespace-pre-wrap">{c.body}</p>}
                        {c.attachments?.length > 0 && (
                            <div className="flex flex-wrap gap-2 mt-1.5">
                                {c.attachments.map((a, i) => (
                                    <a key={i} href={a.dataUrl} target="_blank" rel="noreferrer">
                                        <img src={a.dataUrl} alt={a.name} className="w-16 h-16 object-cover rounded border border-gray-200 hover:opacity-90" />
                                    </a>
                                ))}
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    )
}
function HistoryDock({ history, onRollback, onClose }) {
    return (
        <div className="w-72 flex-shrink-0 bg-white border border-gray-200 rounded flex flex-col">
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
                <h3 className="text-sm font-bold text-gray-800">Version History</h3>
                <button onClick={onClose}><X className="w-4 h-4 text-gray-400 hover:text-gray-600" /></button>
            </div>
            <div className="flex-1 overflow-auto p-3 space-y-3">
                {history.length === 0 && <p className="text-xs text-gray-400 text-center mt-6">No changes yet.</p>}
                {history.map((h) => (
                    <div key={h.id} className="group border-l-2 border-[#358ECD] pl-2">
                        <p className="text-[10px] text-gray-400">{h.time}</p>
                        <p className="text-xs text-gray-700">{h.text}</p>
                        {h.snapshot && (
                            <button onClick={() => onRollback(h)} className="text-[10px] text-[#358ECD] hover:underline opacity-0 group-hover:opacity-100 transition">
                                Roll back to here
                            </button>
                        )}
                    </div>
                ))}
            </div>
        </div>
    )
}

// ── Modals ──
const modalWrap = 'fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-[1100] p-4'
const inputCls = 'w-full border border-gray-300 rounded px-3 py-2 text-sm text-gray-700 focus:outline-none focus:border-[#358ECD]'

function FindAndMarkModal({ series, onCancel, onMark }) {
    const [paramId, setParamId] = useState(series[0]?.id ? String(series[0].id) : '')
    const [condition, setCondition] = useState('between')
    const [v1, setV1] = useState(''); const [v2, setV2] = useState('')
    const [status, setStatus] = useState('')
    const two = CONDITIONS.find((c) => c.id === condition)?.two
    const canMark = paramId && status && v1 !== '' && (!two || v2 !== '')
    return (
        <div className={modalWrap} onClick={onCancel}>
            <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-5" onClick={(e) => e.stopPropagation()}>
                <h3 className="text-lg font-bold text-gray-800 mb-4">Find and Mark</h3>
                <label className="block text-sm text-gray-700 mb-1">Parameter</label>
                <select className={inputCls + ' mb-4'} value={paramId} onChange={(e) => setParamId(e.target.value)}>
                    {!series.length && <option value="">Select Parameter</option>}
                    {series.map((s) => <option key={s.id} value={String(s.id)}>{s.name}{s.unit ? ` (${s.unit})` : ''}</option>)}
                </select>
                <label className="block text-sm text-gray-700 mb-1">Condition</label>
                <select className={inputCls + ' mb-2'} value={condition} onChange={(e) => setCondition(e.target.value)}>
                    {CONDITIONS.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
                </select>
                <div className="flex items-center gap-2 mb-4">
                    <input className={inputCls} placeholder="Value" value={v1} onChange={(e) => setV1(e.target.value)} />
                    {two && <><span className="text-xs text-gray-500">and</span><input className={inputCls} placeholder="Value" value={v2} onChange={(e) => setV2(e.target.value)} /></>}
                </div>
                <label className="block text-sm text-gray-700 mb-1">Mark As</label>
                <select className={inputCls + ' mb-5'} value={status} onChange={(e) => setStatus(e.target.value)}>
                    <option value="">Select Status</option>
                    {MARK_AS.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
                </select>
                <button disabled={!canMark} onClick={() => onMark({ paramId, condition, v1, v2, status })} className="w-full py-2 bg-[#358ECD] text-white rounded font-medium disabled:opacity-40 hover:bg-[#2d7ab5]">MARK</button>
                <button onClick={onCancel} className="w-full mt-2 text-sm text-[#358ECD] hover:underline">Cancel</button>
            </div>
        </div>
    )
}

function MarkRulesModal({ series, rules, onCancel, onSave }) {
    const [list, setList] = useState(rules)
    const [editing, setEditing] = useState(null) // rule being edited/added
    const blankRule = () => ({ id: uid('r'), name: '', paramId: series[0]?.id ? String(series[0].id) : '', condition: 'between', v1: '', v2: '', status: 'erroneous' })
    const two = editing && CONDITIONS.find((c) => c.id === editing.condition)?.two
    return (
        <div className={modalWrap} onClick={onCancel}>
            <div className="bg-white rounded-lg shadow-xl w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
                    <h3 className="text-lg font-bold text-gray-800">Mark Rules</h3>
                    <div className="flex gap-2">
                        <button onClick={() => onSave(list)} className="px-3 py-1 bg-[#358ECD] text-white text-sm rounded hover:bg-[#2d7ab5]">Save</button>
                        <button onClick={onCancel} className="px-3 py-1 border border-gray-300 text-gray-700 text-sm rounded hover:bg-gray-50">Cancel</button>
                    </div>
                </div>
                <div className="px-5 py-4">
                    {!editing ? (
                        <>
                            <p className="text-sm font-semibold text-gray-700 mb-2">Rules</p>
                            <div className="border border-gray-200 rounded min-h-[160px] divide-y divide-gray-100">
                                {list.length === 0 && <p className="text-xs text-gray-400 p-4 text-center">No rules yet.</p>}
                                {list.map((r) => (
                                    <div key={r.id} className="flex items-center justify-between px-3 py-2">
                                        <span className="text-sm text-gray-700">{r.name || '(unnamed rule)'}</span>
                                        <span className="flex gap-3 text-sm">
                                            <button onClick={() => setList(list.filter((x) => x.id !== r.id))} className="text-red-500 hover:underline">Remove</button>
                                            <button onClick={() => setEditing(r)} className="text-[#358ECD] hover:underline">Edit</button>
                                        </span>
                                    </div>
                                ))}
                            </div>
                            <div className="flex justify-end mt-3">
                                <button onClick={() => setEditing(blankRule())} className="px-3 py-1.5 border border-[#358ECD] text-[#358ECD] text-sm rounded hover:bg-[#E8F1F8]">Add Rule</button>
                            </div>
                        </>
                    ) : (
                        <div className="space-y-3">
                            <input className={inputCls} placeholder="Rule name" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} />
                            <select className={inputCls} value={editing.paramId} onChange={(e) => setEditing({ ...editing, paramId: e.target.value })}>
                                {series.map((s) => <option key={s.id} value={String(s.id)}>{s.name}{s.unit ? ` (${s.unit})` : ''}</option>)}
                            </select>
                            <select className={inputCls} value={editing.condition} onChange={(e) => setEditing({ ...editing, condition: e.target.value })}>
                                {CONDITIONS.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
                            </select>
                            <div className="flex items-center gap-2">
                                <input className={inputCls} placeholder="Value" value={editing.v1} onChange={(e) => setEditing({ ...editing, v1: e.target.value })} />
                                {two && <><span className="text-xs text-gray-500">and</span><input className={inputCls} placeholder="Value" value={editing.v2} onChange={(e) => setEditing({ ...editing, v2: e.target.value })} /></>}
                            </div>
                            <select className={inputCls} value={editing.status} onChange={(e) => setEditing({ ...editing, status: e.target.value })}>
                                {MARK_AS.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
                            </select>
                            <div className="flex justify-end gap-2 pt-1">
                                <button onClick={() => setEditing(null)} className="px-3 py-1.5 border border-gray-300 text-gray-700 text-sm rounded">Cancel</button>
                                <button onClick={() => { setList((prev) => prev.some((x) => x.id === editing.id) ? prev.map((x) => x.id === editing.id ? editing : x) : [...prev, editing]); setEditing(null) }} className="px-3 py-1.5 bg-[#358ECD] text-white text-sm rounded">SAVE RULE</button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}

function AddCommentModal({ cellLabel, onCancel, onAdd }) {
    const [body, setBody] = useState('')
    const [attachments, setAttachments] = useState([])
    const [over, setOver] = useState(false)
    const fileRef = useRef(null)

    const addFiles = (files) => {
        Array.from(files || []).forEach((f) => {
            if (!f.type.startsWith('image/')) { alert(`"${f.name}" is not an image.`); return }
            if (f.size > 1.5 * 1024 * 1024) { alert(`"${f.name}" is larger than 1.5 MB — too large to store locally. Please use a smaller image.`); return }
            const reader = new FileReader()
            reader.onload = () => setAttachments((a) => [...a, { name: f.name, dataUrl: reader.result }])
            reader.readAsDataURL(f)
        })
    }

    return (
        <div className={modalWrap} onClick={onCancel}>
            <div className="bg-white rounded-lg shadow-xl w-full max-w-sm p-4" onClick={(e) => e.stopPropagation()}>
                <h3 className="text-base font-bold text-gray-800 mb-3">Add Comment{cellLabel ? ` — ${cellLabel}` : ''}</h3>
                <textarea rows={3} value={body} onChange={(e) => setBody(e.target.value)} placeholder="Comment" className={inputCls + ' resize-none mb-2'} />
                <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => { addFiles(e.target.files); e.target.value = '' }} />
                <div
                    onClick={() => fileRef.current?.click()}
                    onDragOver={(e) => { e.preventDefault(); setOver(true) }}
                    onDragLeave={() => setOver(false)}
                    onDrop={(e) => { e.preventDefault(); setOver(false); addFiles(e.dataTransfer.files) }}
                    className={`border border-dashed rounded p-4 text-center text-xs cursor-pointer mb-2 transition ${over ? 'border-[#358ECD] bg-[#E8F1F8] text-[#358ECD]' : 'border-gray-300 text-gray-400 hover:bg-gray-50'}`}>
                    Drag Attachments or Upload Photos
                </div>
                {attachments.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-3">
                        {attachments.map((a, i) => (
                            <div key={i} className="relative">
                                <img src={a.dataUrl} alt={a.name} className="w-14 h-14 object-cover rounded border border-gray-200" />
                                <button onClick={() => setAttachments((arr) => arr.filter((_, j) => j !== i))} className="absolute -top-1.5 -right-1.5 bg-white border border-gray-300 rounded-full w-4 h-4 flex items-center justify-center text-gray-500 hover:text-red-500"><X className="w-2.5 h-2.5" /></button>
                            </div>
                        ))}
                    </div>
                )}
                <div className="flex justify-end gap-2">
                    <button onClick={onCancel} className="px-4 py-1.5 border border-gray-300 text-gray-700 text-sm rounded">CANCEL</button>
                    <button disabled={!body.trim() && attachments.length === 0} onClick={() => onAdd(body.trim(), attachments)} className="px-4 py-1.5 bg-[#358ECD] text-white text-sm rounded disabled:opacity-40">COMMENT</button>
                </div>
            </div>
        </div>
    )
}
